/**
 * Reference frames on a treatment's shots.
 *
 * We link to frames; we never store them. A frame is an https image address,
 * plus an optional link to the page it came from.
 *
 * What Frameset (frameset.app) hands over, measured 14 Oct 2026:
 * - Dragging a frame gives `text/uri-list` and `text/plain`, both the image
 *   address, and nothing else: no HTML, no link to the frame's page.
 * - Its images live at https://d13mryl9xv19vu.cloudfront.net/{uuid}_{size}.{ext}.
 *   `_xl.jpeg` is full size (1-2 MB); `_sm.avif` is about 60 KB and 960px
 *   wide, which is plenty for an editor thumbnail.
 * - The CDN serves real browsers from any site, but answers servers and
 *   headless browsers with a bot challenge. Do not fetch frames server-side.
 *
 * Everything Frameset-specific lives in this file.
 */

const FRAMESET_IMAGE = /^https:\/\/d13mryl9xv19vu\.cloudfront\.net\/([0-9a-f-]{36})_(?:xs|sm|md|lg|xl)\.(?:jpe?g|avif|webp|png)$/i;

export const MAX_REFERENCES_PER_SHOT = 50; // the backend's limit too

/** An https URL, trimmed, or null. */
export function toHttpsUrl(text) {
  const candidate = (text || '').trim();
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

/**
 * The image addresses in a drop or paste, in order, without duplicates.
 *
 * `data` is anything with `types` and `getData(type)`: a DataTransfer from a
 * drop or a ClipboardData from a paste. Prefers text/uri-list, then image
 * sources in text/html (other sites drag that), then text/plain.
 */
export function urlsFromTransfer(data) {
  const types = Array.from(data?.types || []);
  const found = [];

  if (types.includes('text/uri-list')) {
    data.getData('text/uri-list')
      .split(/\r?\n/)
      .filter((line) => line && !line.startsWith('#'))
      .forEach((line) => found.push(line));
  }
  if (!found.length && types.includes('text/html')) {
    const doc = new window.DOMParser().parseFromString(data.getData('text/html'), 'text/html');
    doc.querySelectorAll('img[src]').forEach((img) => found.push(img.getAttribute('src')));
  }
  if (!found.length && types.includes('text/plain')) {
    data.getData('text/plain').split(/\s+/).forEach((word) => found.push(word));
  }

  return [...new Set(found.map(toHttpsUrl).filter(Boolean))];
}

export const isFramesetImage = (url) => FRAMESET_IMAGE.test(url || '');

/**
 * The address to show in the editor. For a Frameset frame that is its small
 * AVIF instead of the full-size JPEG; anything else is shown as linked.
 */
export function thumbnailUrl(url) {
  const match = FRAMESET_IMAGE.exec(url || '');
  return match ? `https://d13mryl9xv19vu.cloudfront.net/${match[1]}_sm.avif` : url;
}

/** Add new frames to a shot's list, skipping ones it already has and stopping at the limit. */
export function appendReferences(existing, urls) {
  const have = new Set((existing || []).map((r) => r.image_url));
  const room = MAX_REFERENCES_PER_SHOT - (existing || []).length;
  const fresh = urls.filter((u) => !have.has(u)).slice(0, Math.max(0, room));
  return [
    ...(existing || []),
    ...fresh.map((image_url) => ({ id: crypto.randomUUID(), image_url, source_url: null, note: '' })),
  ];
}

/**
 * Why these shots' frames cannot be saved, or null. The backend refuses the
 * same things; this says so in words before the request is made.
 */
export function referencesError(shots, numbers = {}) {
  for (const shot of shots || []) {
    for (const ref of shot.references || []) {
      if (!toHttpsUrl(ref.image_url)) return `Shot ${numbers[shot.id] || ''}: a frame's link must start with https://`;
      if (ref.source_url && !toHttpsUrl(ref.source_url)) {
        return `Shot ${numbers[shot.id] || ''}: source links must start with https://`;
      }
    }
  }
  return null;
}
